import {memo, useContext} from 'react'

import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import {useGroups} from '@adv-mt/mt-list-model-details/lib/useGroups.js'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'
import {Button, BUTTON_SHAPES, BUTTON_SIZES, BUTTON_VARIANTS} from '@adv-mt/ui/button'
import {Headline1} from '@adv-mt/ui/typography'

import {DRAWER_TYPES} from '../../drawers/config.js'
import CharacteristicItem from './CharacteristicItem.js'
import {BASE_CLASS} from './config.js'
import IncludedExtras from './IncludedExtras.js'
import useCharacteristics from './useCharacteristics.js'

const CharacteristicsPanel = ({
  ad = {},
  groupedSpecifications,
  onOpenDrawer,
  optionalEquipment = [],
  vehicleInfo = {}
}) => {
  const {domain, i18n} = useContext(Context)
  const {track: analyticsTrack} = useAnalyticsTrack()

  const config = domain.get('config')
  const featuresSectionId = config.get('COMMON').AD_DETAIL_PAGE_SECTIONS.AD_DETAIL_PAGE_SECTION_IDS.FEATURES
  const {HIT_INFORMATIONS, TRACKING_EVENT_NAMES} = config.get('tracking')

  const isHigherThanMobile = useMediaQuery(useMediaQueryBreakpoints.sm, {
    defaultMatches: !config.get('isMobile')
  })

  const {items} = useCharacteristics({ad, vehicleInfo})

  const hasSpecifications = useGroups(groupedSpecifications ?? {}).some(group => group?.value?.some(Boolean))
  const {HAS_AD_DETAIL_TECHNICAL_DATASHEET_ENABLED} = config.get('features')
  const hasTechnicalDetails =
    HAS_AD_DETAIL_TECHNICAL_DATASHEET_ENABLED &&
    (hasSpecifications ||
      Boolean(vehicleInfo.equipment?.standard?.length) ||
      Boolean(vehicleInfo.equipment?.optional?.length))

  const trackButtonClicked = async hitInformation => {
    const eventData = await domain.get('get_event_tracking_data_use_case').execute({
      event: TRACKING_EVENT_NAMES.AD_DETAIL_BUTTON_CLICKED,
      hitInformation
    })

    analyticsTrack(eventData)
  }

  const handleShowAllExtras = onOpenDrawer
    ? () => {
        trackButtonClicked(HIT_INFORMATIONS.AD_DETAIL_SHOW_EXTRAS)
        onOpenDrawer({data: optionalEquipment, type: DRAWER_TYPES.INCLUDED_EXTRAS})
      }
    : undefined

  const handleShowTechnicalDetails = onOpenDrawer
    ? () => {
        trackButtonClicked(HIT_INFORMATIONS.AD_DETAIL_SHOW_TECHNICAL_DATASHEET)
        onOpenDrawer({
          data: {
            groupedSpecifications,
            standardEquipment: vehicleInfo.equipment?.standard ?? [],
            optionalEquipment: vehicleInfo.equipment?.optional ?? []
          },
          type: DRAWER_TYPES.TECHNICAL_DETAILS
        })
      }
    : undefined

  if (!items.length) return null

  return (
    <section id={featuresSectionId} className={BASE_CLASS}>
      <Headline1 as="h2" className={`${BASE_CLASS}-title`}>
        {i18n.t('COMMON.CHARACTERISTICS')}
      </Headline1>
      <ul className={`${BASE_CLASS}-grid`}>
        {items.map(({icon, label, value}, index) => (
          <CharacteristicItem key={index} icon={icon} label={label} value={value} />
        ))}
      </ul>
      <IncludedExtras onShowAll={handleShowAllExtras} optionalEquipment={optionalEquipment} />
      {hasTechnicalDetails && (
        <div className={`${BASE_CLASS}-ctaWrapper`}>
          <Button
            onClick={handleShowTechnicalDetails}
            shape={BUTTON_SHAPES.CIRCLE}
            variant={BUTTON_VARIANTS.OUTLINE}
            size={isHigherThanMobile ? BUTTON_SIZES.MEDIUM : BUTTON_SIZES.LARGE}
          >
            {i18n.t('COMMON.SHOW_TECHNICAL_DATASHEET')}
          </Button>
        </div>
      )}
    </section>
  )
}

CharacteristicsPanel.displayName = 'CharacteristicsPanel'
CharacteristicsPanel.propTypes = {
  ad: PropTypes.object,
  groupedSpecifications: PropTypes.object,
  onOpenDrawer: PropTypes.func,
  optionalEquipment: PropTypes.array,
  vehicleInfo: PropTypes.object
}

export default memo(CharacteristicsPanel)
